/**
 * Laying typed arrays over DICOM pixel bytes.
 *
 * The typed model holds a value as a view over the buffer the file was parsed
 * from, so pixel data can usually be read without copying it anywhere. Usually,
 * not always: a DICOM value field is aligned to two bytes, so a 16-bit view
 * always fits one but a 32-bit view does not, and constructing a misaligned
 * typed array throws rather than reading at the wrong offset.
 *
 * Where the alignment does not allow a view, the bytes are copied into a buffer
 * that does — the same copy the keyword-addressed projection made every time,
 * now paid only when the file's own layout forces it.
 */

/** Bytes per value for the pixel encodings the application reads. */
export type PixelValueWidth = 1 | 2 | 4;

/**
 * A byte range aligned well enough to carry values of the given width.
 *
 * Returns the original view where it already is, so the common case costs
 * nothing.
 */
export function alignedFor(
    bytes: Uint8Array,
    width: PixelValueWidth
): Uint8Array {
    if (bytes.byteOffset % width === 0) {
        return bytes;
    }
    return new Uint8Array(
        bytes.buffer.slice(
            bytes.byteOffset,
            bytes.byteOffset + bytes.byteLength
        )
    );
}

/** Reads a byte range as unsigned 16-bit values. */
export function asUint16(bytes: Uint8Array): Uint16Array {
    const aligned = alignedFor(bytes, 2);
    return new Uint16Array(
        aligned.buffer,
        aligned.byteOffset,
        Math.floor(aligned.byteLength / 2)
    );
}

/** Reads a byte range as signed 16-bit values. */
export function asInt16(bytes: Uint8Array): Int16Array {
    const aligned = alignedFor(bytes, 2);
    return new Int16Array(
        aligned.buffer,
        aligned.byteOffset,
        Math.floor(aligned.byteLength / 2)
    );
}

/** Reads a byte range as unsigned 32-bit values. */
export function asUint32(bytes: Uint8Array): Uint32Array {
    const aligned = alignedFor(bytes, 4);
    return new Uint32Array(
        aligned.buffer,
        aligned.byteOffset,
        Math.floor(aligned.byteLength / 4)
    );
}

/** The byte range of an ArrayBuffer or an existing view, without copying. */
export function bytesOf(source: ArrayBuffer | Uint8Array): Uint8Array {
    return source instanceof Uint8Array ? source : new Uint8Array(source);
}
